import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  ApiKeyInfo,
  AssignRoleRequest,
  CreateUserRequest,
  DeleteUserRequest,
  DeleteUserResponse,
  GetUsersRequest,
  GetUsersResponse,
  ImpersonateResponse,
  ListApiKeysResponse,
  UpdateUserRequest,
  User,
  UserRolesResponse,
} from './users.type';
import { Permissions, UserRole } from '../auth/auth.type';

@Injectable({
  providedIn: 'root'
})
export class UsersService {
  private apiUrl = environment.apiBaseURL + '/auth/users';
  private rolesUrl = environment.apiBaseURL + '/auth/roles';
  private http = inject(HttpClient);

  getUsers(request: GetUsersRequest = {}): Observable<GetUsersResponse> {
    let params = new HttpParams();
    if (request.role) params = params.set('role', request.role);
    if (request.search) params = params.set('search', request.search);
    if (request.pageIndex !== undefined) params = params.set('pageIndex', request.pageIndex);
    if (request.pageSize !== undefined) params = params.set('pageSize', request.pageSize);
    return this.http.get<GetUsersResponse>(this.apiUrl, { params });
  }

  createUser(request: CreateUserRequest): Observable<User> {
    return this.http.post<User>(this.apiUrl, request);
  }

  updateUser(userId: string, request: UpdateUserRequest): Observable<User> {
    return this.http.put<User>(`${this.apiUrl}/${userId}`, request);
  }

  deleteUser(userId: string, request: DeleteUserRequest): Observable<DeleteUserResponse> {
    return this.http.delete<DeleteUserResponse>(`${this.apiUrl}/${userId}`, { body: request });
  }

  updateUserActive(userId: string, active: boolean): Observable<User> {
    return this.http.patch<User>(`${this.apiUrl}/${userId}/active`, { active });
  }

  impersonate(userId: string): Observable<ImpersonateResponse> {
    return this.http.post<ImpersonateResponse>(`${this.apiUrl}/${userId}/impersonate`, {});
  }

  // ── Role management (SuperAdmin only) ───────────────────────────────────

  getAllRoles(): Observable<{ result: UserRole[] }> {
    return this.http.get<{ result: UserRole[] }>(this.rolesUrl);
  }

  getUserRoles(userId: string): Observable<{ result: UserRolesResponse }> {
    return this.http.get<{ result: UserRolesResponse }>(`${this.apiUrl}/${userId}/roles`);
  }

  assignRole(userId: string, request: AssignRoleRequest): Observable<{ result: string }> {
    return this.http.post<{ result: string }>(`${this.apiUrl}/${userId}/roles`, request);
  }

  removeRole(userId: string, role: UserRole): Observable<{ result: string }> {
    return this.http.delete<{ result: string }>(`${this.apiUrl}/${userId}/roles/${role}`);
  }

  // ── Admin permissions (SuperAdmin only) ─────────────────────────────────

  getUserPermissions(userId: string): Observable<{ result: { permissions: Permissions } }> {
    return this.http.get<{ result: { permissions: Permissions } }>(`${this.apiUrl}/${userId}/permissions`);
  }

  setUserPermissions(userId: string, permissions: Permissions): Observable<{ result: { permissions: Permissions } }> {
    return this.http.put<{ result: { permissions: Permissions } }>(`${this.apiUrl}/${userId}/permissions`, { permissions });
  }

  // ── API keys (SuperAdmin only) ──────────────────────────────────────────

  listApiKeys(): Observable<{ result: ListApiKeysResponse }> {
    return this.http.get<{ result: ListApiKeysResponse }>(`${this.apiUrl}/api-keys`);
  }

  generateApiKey(userId: string): Observable<{ result: ApiKeyInfo }> {
    return this.http.post<{ result: ApiKeyInfo }>(`${this.apiUrl}/${userId}/api-key`, {});
  }

  revokeApiKey(userId: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.apiUrl}/${userId}/api-key`);
  }
}
